/**
 * Captura de rosto pela webcam, atrás de uma interface.
 *
 * A Neuro TEM reconhecimento facial em tempo real, mas ele exige ambiente
 * dedicado e compromisso comercial, e não estará disponível em 30/09. Até lá
 * quem reconhece é um mock no backend (PBI #5030). Este hook não sabe disso e
 * não deve saber: ele captura um quadro e entrega bytes. Trocar o mock pelo
 * Face ID real não muda uma linha daqui.
 *
 * Por isso a captura mora num hook e não dentro da tela de login. A mesma
 * captura é usada em três lugares — primeiro acesso (PBI-04), login diário
 * (PBI-05) e reconfirmação de sessão (PBI-07). Escrita dentro da tela, seria
 * copiada três vezes e divergiria na segunda.
 *
 * ## O que este hook não faz
 *
 * Não decide quem é a pessoa, não chama a API de login, não guarda a imagem.
 * O quadro vira um Blob, é entregue a quem chamou e some. Biometria é dado
 * pessoal sensível na LGPD; um `useState` guardando o último quadro
 * "para depurar" é exatamente como esse tipo de dado vaza para um relatório
 * de erro.
 *
 * ## Os estados são cinco, e isso é proposital
 *
 * `idle`, `starting`, `ready`, `capturing`, `error`. Quem consome precisa
 * distinguir "ainda pedindo permissão" de "pronto para capturar": a primeira
 * mostra instrução, a segunda habilita o botão. E `error` carrega o motivo,
 * porque "permissão negada" e "não há câmera" pedem textos diferentes — e
 * nenhum dos dois é "não reconheci você", que é resposta do backend.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export const CAPTURE_IDLE = 'idle';
export const CAPTURE_STARTING = 'starting';
export const CAPTURE_READY = 'ready';
export const CAPTURE_CAPTURING = 'capturing';
export const CAPTURE_ERROR = 'error';

export const ERROR_DENIED = 'denied';
export const ERROR_NO_CAMERA = 'no_camera';
export const ERROR_UNSUPPORTED = 'unsupported';
// A câmera existe mas outro programa (Teams, Zoom) a está usando.
export const ERROR_IN_USE = 'in_use';
// A câmera ligou e depois parou de mandar imagem: cabo solto, dispositivo
// desconectado no meio da captura.
export const ERROR_LOST = 'lost';
export const ERROR_UNKNOWN = 'unknown';

// O backend recusa quadros abaixo de 1 KiB como falha de captura. Enviar um
// quadro preto e deixar o servidor recusar gastaria uma ida e volta para
// dizer o que dá para saber aqui.
export const MIN_FRAME_BYTES = 1024;

const DEFAULT_CONSTRAINTS = {
  video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
  audio: false,
};

function classifyError(error) {
  if (!error || !error.name) return ERROR_UNKNOWN;
  if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
    return ERROR_DENIED;
  }
  if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
    return ERROR_NO_CAMERA;
  }
  if (error.name === 'NotReadableError' || error.name === 'TrackStartError' || error.name === 'AbortError') {
    return ERROR_IN_USE;
  }
  return ERROR_UNKNOWN;
}

export default function useFaceCapture({ constraints = DEFAULT_CONSTRAINTS } = {}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState(CAPTURE_IDLE);
  const [errorKind, setErrorKind] = useState(null);

  const stop = useCallback(() => {
    // Desligar a câmera é obrigação, não cortesia: a luz acesa depois que a
    // tela fechou é o que faz o médico desconfiar do sistema.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus(CAPTURE_IDLE);
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorKind(ERROR_UNSUPPORTED);
      setStatus(CAPTURE_ERROR);
      return false;
    }
    setErrorKind(null);
    setStatus(CAPTURE_STARTING);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      // Se a imagem parar depois de a câmera ligar, a tela precisa dizer que
      // foi a câmera — e não esperar a próxima captura falhar sem motivo.
      stream.getVideoTracks().forEach((track) => {
        track.addEventListener('ended', () => {
          if (streamRef.current !== stream) return; // parada por nós, via stop()
          streamRef.current = null;
          setErrorKind(ERROR_LOST);
          setStatus(CAPTURE_ERROR);
        });
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // O play pode ser rejeitado por política de autoplay; o quadro ainda
        // é capturável, então isso não é motivo para falhar a captura.
        const playing = videoRef.current.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      }
      setStatus(CAPTURE_READY);
      return true;
    } catch (error) {
      setErrorKind(classifyError(error));
      setStatus(CAPTURE_ERROR);
      return false;
    }
  }, [constraints]);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || status !== CAPTURE_READY) return null;

    setStatus(CAPTURE_CAPTURING);
    try {
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(video, 0, 0, width, height);

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });

      if (!blob || blob.size < MIN_FRAME_BYTES) {
        // Quadro preto: a câmera abriu mas não entregou imagem. É erro de
        // captura, não rosto desconhecido — e a tela dirá coisas diferentes.
        setErrorKind(ERROR_NO_CAMERA);
        setStatus(CAPTURE_ERROR);
        return null;
      }

      setStatus(CAPTURE_READY);
      return blob;
    } catch (error) {
      setErrorKind(classifyError(error));
      setStatus(CAPTURE_ERROR);
      return null;
    }
  }, [status]);

  // A câmera desliga quando o componente sai, inclusive se ele sair no meio
  // de uma captura.
  useEffect(() => stop, [stop]);

  return {
    videoRef,
    status,
    errorKind,
    isReady: status === CAPTURE_READY,
    start,
    capture,
    stop,
  };
}
